/**
 * Servicio para interactuar con la API de Open Food Facts.
 * Proporciona métodos para obtener información nutricional a partir de un código de barras.
 */
import axios from "axios";

const BASE_URL = "https://world.openfoodfacts.org/api/v2/product";

/**
 * Obtiene los detalles de un producto mediante su código de barras.
 * @param {string} barcode Código de barras del producto.
 * @returns {Promise<Object|null>} Información del producto o null si no se encuentra.
 */
export async function getProductInfoByBarcode(barcode) {
    if (!barcode) return null;
    try {
        const response = await axios.get(`${BASE_URL}/${barcode}.json`);
        if (response.data?.status === 1) {
            const p = response.data.product;
            return {
                nombre: p.product_name || p.generic_name || "Producto desconocido",
                marca: p.brands || "",
                imagen: p.image_front_url || p.image_url || "",
                nutriscore: p.nutriscore_grade || "unknown",
                nova: p.nova_group || null,
                kcal: p.nutriments?.["energy-kcal_100g"] || null,
                grasas: p.nutriments?.fat_100g || null,
                azucares: p.nutriments?.sugars_100g || null,
                proteinas: p.nutriments?.proteins_100g || null,
                sal: p.nutriments?.salt_100g || null,
                aditivos: p.additives_n || 0,
                // Intenta español primero, luego inglés, luego cualquier idioma disponible
                ingredientes: p.ingredients_text_es || p.ingredients_text_en || p.ingredients_text || "",
                es_saludable: determinarSiEsSaludable(p.nutriscore_grade, p.nova_group)
            };
        }
        return null;
    } catch (error) {
        console.error("Error al consultar Open Food Facts:", error);
        return null;
    }
}

/**
 * Determina de forma sencilla si un producto es saludable basándose en Nutri-Score y grado NOVA.
 * @param {string} nutriscore Grado de Nutri-Score (a-e).
 * @param {number|string} nova Grado NOVA (1-4).
 * @returns {boolean} True si se considera saludable.
 */
function determinarSiEsSaludable(nutriscore, nova) {
    const ns = (nutriscore || "e").toLowerCase();
    const nv = Number(nova || 4);
    
    // Se considera saludable si es Nutri-Score A o B, y no es ultraprocesado (NOVA 4)
    if ((ns === "a" || ns === "b") && nv < 4) {
        return true;
    }
    return false;
}

const foodApiService = {
    getProductInfoByBarcode
};

export default foodApiService;
